import { Dispatch, SetStateAction, useEffect, useMemo } from "react";
import type { IconMappingProps } from "@foodpilot/foods";

import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import type { ProductLivePreview } from "src/api/client-api/products/useProductLivePreview";
import {
    type TransportCircuit,
    useTransportCircuits,
    useUpsertTransportCircuit,
} from "src/api/client-api/transport/useTransportCircuits";
import {
    isTransportReferenceApplicableToStage,
    type TransportReference,
    useSearchTransportReferences,
} from "src/api/sure-api/useSearchTransportReferences";
import { useSearchTransportModes } from "src/api/sure-api/useSearchTransportModes";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import type {
    ProductFormProductTransport,
    ProductFormTransportStage,
    ProductFormType,
} from "src/pages/Product/ProductForm/ProductForm.types";
import {
    findTransportOptionByReferenceId,
    findTransportOptionForPersistedTransport,
} from "./productTransportOptionMatching";
import { buildProductTransportSummary } from "./productTransportSummary";

export type TransportAnchor =
    | {
          type: "ingredient";
          value: { "@id": IRI; id: number } | null;
      }
    | {
          type: "packaging";
          value: { "@id": IRI; id: number } | null;
      }
    | {
          type: "product";
          value?: null;
      };

export type TransportMaterializationPendingChange = (selectorId: string, pending: boolean) => void;

export type ProductTransportInlineSelectorProps = {
    product: ProductFormType;
    setProduct: Dispatch<SetStateAction<ProductFormType>>;
    stage: ProductFormTransportStage;
    anchor: TransportAnchor;
    draftAnchorKey?: string | null;
    resolvedTransport?: ProductLivePreview["resolvedTransports"][number];
    label?: string;
    disabled?: boolean;
    lineScore?: string | null;
    scoreUnit?: string;
    alignScoreColumn?: boolean;
    scoreOnly?: boolean;
    routePickerLayout?: boolean;
    originCountryCode?: string | null;
    onMaterializationPendingChange?: TransportMaterializationPendingChange;
};

export type TransportOption =
    | {
          kind: "reference";
          id: number;
          label: string;
          reference: TransportReference;
      }
    | {
          kind: "circuit";
          id: number;
          label: string;
          circuit: TransportCircuit;
      };

export type TransportSummary = {
    modeIcons: Array<{ key: string; icon: IconMappingProps; label: string }>;
    distanceKm: number | null;
};

export type TransportMaterializationFailure = {
    contextKey: string;
    failureKind: "ambiguous" | "contract" | "refresh" | "request";
    option: TransportOption;
};

const DEFAULT_TRANSPORT_REFERENCE_TAG = "ecobalyse";
export const pendingReferenceCircuitMaterializations = new Map<string, Promise<TransportCircuit>>();

const isDefaultTransportReference = (reference: TransportReference): boolean =>
    reference.tags.includes(DEFAULT_TRANSPORT_REFERENCE_TAG);

export const referenceLabel = (reference: TransportReference): string => {
    const route = [reference.originCountryCode, reference.destinationCountryCode].filter(Boolean).join(" → ");
    const name = reference.name ?? reference.code;

    return route ? `${name} (${route})` : name;
};

export const toFormCircuit = (circuit: TransportCircuit): ProductFormProductTransport["transportCircuit"] => ({
    "@id": circuit["@id"],
    id: circuit.id,
    name: circuit.name ?? null,
    linkMode: circuit.linkMode,
    sourceReferenceCircuitId: circuit.sourceReferenceCircuitId ?? null,
});

type ProductTransportSelectorDataOptions = {
    anchor: TransportAnchor;
    draftAnchorKey: string | null;
    product: ProductFormType;
    resolvedTransport: ProductLivePreview["resolvedTransports"][number] | undefined;
    stage: ProductFormTransportStage;
};

export const useProductTransportSelectorData = (options: ProductTransportSelectorDataOptions) => {
    const { anchor, draftAnchorKey, product, resolvedTransport, stage } = options;
    const { selectedCompany } = useFoodpilotContext();
    const orgId = selectedCompany?.id;
    const orgIri = selectedCompany?.["@id"];
    const anchorValueIri = anchor.value?.["@id"];
    const anchorValueId = anchor.value?.id;
    const { isFeatureActivated } = useApplicationParameters();
    const isManageMyTransportEnabled = isFeatureActivated("FS-manage-my-transport");
    const transports = useMemo(() => product.productTransports ?? [], [product.productTransports]);
    const {
        transportCircuits: loadedTransportCircuits,
        isError: areTransportCircuitsErrored,
        isLoading: areTransportCircuitsLoading,
        isSuccess: areTransportCircuitsLoaded,
        refetch: refetchTransportCircuits,
    } = useTransportCircuits({
        organizationId: orgId,
        enabled: orgId !== undefined,
    });
    const transportCircuits = useMemo(() => loadedTransportCircuits ?? [], [loadedTransportCircuits]);
    const {
        transportReferences,
        isError: areTransportReferencesErrored,
        isLoading: areTransportReferencesLoading,
        isSuccess: areTransportReferencesLoaded,
        refetch: refetchTransportReferences,
    } = useSearchTransportReferences({
        enabled: !isManageMyTransportEnabled,
        applicableStage: stage,
    });
    const { transportModes } = useSearchTransportModes({ enabled: isManageMyTransportEnabled });
    const upsertTransportCircuit = useUpsertTransportCircuit();

    useEffect(() => {
        if (orgIri === undefined) {
            return;
        }

        transportCircuits.forEach((circuit) => {
            if (circuit.sourceReferenceCircuitId != null) {
                pendingReferenceCircuitMaterializations.delete(`${orgIri}:${circuit.sourceReferenceCircuitId}`);
            }
        });
    }, [orgIri, transportCircuits]);

    const selectedTransport = useMemo(
        () =>
            transports.find((transport) => {
                if (transport.stage !== stage) {
                    return false;
                }

                if (anchor.type === "ingredient") {
                    if (anchor.value === null && draftAnchorKey !== null) {
                        return transport.draftProductIngredientKey === draftAnchorKey;
                    }

                    return (
                        transport.productIngredient?.["@id"] === anchor.value?.["@id"] ||
                        transport.productIngredient?.id === anchor.value?.id
                    );
                }

                if (anchor.type === "packaging") {
                    return (
                        transport.productPackaging?.["@id"] === anchor.value?.["@id"] ||
                        transport.productPackaging?.id === anchor.value?.id
                    );
                }

                return transport.productIngredient == null && transport.productPackaging == null;
            }),
        [anchor, draftAnchorKey, stage, transports],
    );

    const compatibleReferences = useMemo(
        () =>
            transportReferences.filter(
                (reference) =>
                    isDefaultTransportReference(reference) && isTransportReferenceApplicableToStage(reference, stage),
            ),
        [stage, transportReferences],
    );
    const transportOptions = useMemo<TransportOption[]>(() => {
        if (isManageMyTransportEnabled) {
            return transportCircuits.map((circuit) => ({
                kind: "circuit",
                id: circuit.id,
                label: circuit.name ?? `#${circuit.id}`,
                circuit,
            }));
        }

        return compatibleReferences.map((reference) => ({
            kind: "reference",
            id: reference.id,
            label: referenceLabel(reference),
            reference,
        }));
    }, [compatibleReferences, isManageMyTransportEnabled, transportCircuits]);

    const selectedOption = useMemo(() => {
        if (selectedTransport === undefined) {
            return null;
        }

        return findTransportOptionForPersistedTransport(transportOptions, selectedTransport.transportCircuit);
    }, [transportOptions, selectedTransport]);

    const resolvedOption = useMemo(
        () => findTransportOptionByReferenceId(transportOptions, resolvedTransport?.transportReferenceId),
        [transportOptions, resolvedTransport?.transportReferenceId],
    );

    const hasPersistedSelection = selectedTransport !== undefined;
    const effectiveSelectedOption =
        selectedOption ??
        (!hasPersistedSelection && !isManageMyTransportEnabled ?
            (resolvedOption ?? (transportOptions.length === 1 ? transportOptions[0] : null))
        :   null);
    const persistedTransportLabel =
        selectedTransport?.transportCircuit.name ??
        (selectedTransport !== undefined ? `#${selectedTransport.transportCircuit.id}` : null);

    const summary: TransportSummary = useMemo(
        () =>
            buildProductTransportSummary({
                hasPersistedSelection,
                selectedOption,
                effectiveSelectedOption,
                resolvedTransport,
                stage,
                transportModes,
            }),
        [effectiveSelectedOption, hasPersistedSelection, resolvedTransport, selectedOption, stage, transportModes],
    );

    return {
        anchorValueId,
        anchorValueIri,
        areTransportCircuitsErrored,
        areTransportCircuitsLoaded,
        areTransportCircuitsLoading,
        areTransportReferencesErrored,
        areTransportReferencesLoaded,
        areTransportReferencesLoading,
        effectiveSelectedOption,
        hasPersistedSelection,
        isManageMyTransportEnabled,
        options: transportOptions,
        orgId,
        orgIri,
        persistedTransportLabel,
        refetchTransportCircuits,
        refetchTransportReferences,
        resolvedOption,
        selectedOption,
        selectedTransport,
        summary,
        transportCircuits,
        upsertTransportCircuit,
    };
};
